import { useEffect, useMemo, useState } from "react";
import api from "../services/api";

const CACHE_TTL_MS = 5 * 60 * 1000;
const STORAGE_KEY = "rmc2.system_announcement.cache";
const DISMISS_KEY = "rmc2.system_announcement.dismiss";

const LEVEL_STYLES = {
  info: "border-sky-200 bg-sky-50 text-sky-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-rose-200 bg-rose-50 text-rose-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
};

const parseSchedule = (startAt, endAt) => {
  if (!startAt && !endAt) {
    return null;
  }

  const formatDate = (value) => {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toLocaleString();
  };

  const startLabel = formatDate(startAt);
  const endLabel = formatDate(endAt);

  if (startLabel && endLabel) {
    return `Schedule: ${startLabel} - ${endLabel}`;
  }
  if (startLabel) {
    return `Starts: ${startLabel}`;
  }
  if (endLabel) {
    return `Ends: ${endLabel}`;
  }
  return null;
};

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const [dismissSignature, setDismissSignature] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadFromCache = () => {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (!cached) {
        return null;
      }

      try {
        const parsed = JSON.parse(cached);
        if (!parsed?.fetchedAt || !parsed?.data) {
          return null;
        }

        if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) {
          return null;
        }

        return parsed.data;
      } catch (error) {
        return null;
      }
    };

    const saveToCache = (data) => {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          fetchedAt: Date.now(),
          data,
        })
      );
    };

    const fetchAnnouncement = async () => {
      const cached = loadFromCache();
      if (cached && isMounted) {
        setAnnouncement(cached);
        return;
      }

      try {
        const response = await api.get("/system-announcement");
        const data = response.data?.data;
        if (!isMounted) {
          return;
        }
        setAnnouncement(data || null);
        if (data) {
          saveToCache(data);
        }
      } catch (error) {
        return;
      }
    };

    fetchAnnouncement();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!announcement) {
      setDismissedUntil(0);
      setDismissSignature("");
      return;
    }

    const signature = [
      announcement.level || "info",
      announcement.message || "",
      announcement.start_at || "",
      announcement.end_at || "",
    ].join("|");

    setDismissSignature(signature);

    const cached = localStorage.getItem(DISMISS_KEY);
    if (!cached) {
      setDismissedUntil(0);
      return;
    }

    try {
      const parsed = JSON.parse(cached);
      if (!parsed?.signature || !parsed?.until) {
        setDismissedUntil(0);
        return;
      }
      if (parsed.signature !== signature) {
        setDismissedUntil(0);
        return;
      }
      if (Date.now() >= parsed.until) {
        setDismissedUntil(0);
        return;
      }
      setDismissedUntil(parsed.until);
    } catch (error) {
      setDismissedUntil(0);
    }
  }, [announcement]);

  const scheduleText = useMemo(() => {
    if (!announcement) {
      return null;
    }
    return parseSchedule(announcement.start_at, announcement.end_at);
  }, [announcement]);

  if (!announcement?.is_active || !announcement?.message) {
    return null;
  }

  const level = announcement.level || "info";
  const levelStyle = LEVEL_STYLES[level] || LEVEL_STYLES.info;
  const canDismiss = announcement.allow_dismiss;

  if (canDismiss && dismissedUntil && Date.now() < dismissedUntil) {
    return null;
  }

  const handleDismiss = () => {
    if (!announcement || !canDismiss) {
      return;
    }

    const endAt = announcement.end_at ? new Date(announcement.end_at) : null;
    const until = endAt && !Number.isNaN(endAt.getTime())
      ? endAt.getTime()
      : Date.now() + 6 * 60 * 60 * 1000;

    localStorage.setItem(
      DISMISS_KEY,
      JSON.stringify({
        signature: dismissSignature,
        until,
      })
    );
    setDismissedUntil(until);
  };

  return (
    <div className={`w-full border-b ${levelStyle}`}>
      <div className="mx-auto flex w-full flex-col gap-2 px-4 py-2 text-sm lg:flex-row lg:items-center lg:justify-between lg:px-6">
        <div className="flex flex-col gap-1">
          <span className="font-semibold capitalize">{level}</span>
          <span>{announcement.message}</span>
        </div>
        <div className="flex flex-col items-start gap-2 text-xs font-medium text-slate-700 lg:items-end">
          {scheduleText ? <span>{scheduleText}</span> : null}
          {canDismiss ? (
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              Dismiss
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
